// Sci-fi constellation network for the landing hero — denser, layered
// version. Matches the interior of /hero-artwork.jpg but with extra
// depth and motion. Pure SVG + CSS, no JS animation loop.
//
// Layers (back-to-front):
//   1. Star dust   — ~80 tiny static-ish dots, slow random twinkle
//   2. Far mesh    — ~50 dim nodes joined by thin low-opacity edges
//   3. Near mesh   — ~25 bright cyan nodes with glow halos + edges
//   4. Data packets — bright pips moving along a few near-mesh paths
//   5. Binary rain — vertical strings of 0/1 drifting top → bottom
//   6. Hex reticles + scan sweep — accent overlays in the corners
//
// All sizing is in viewBox units (1600×900). The wrapper stretches
// the SVG to cover the hero; preserveAspectRatio="xMidYMid slice"
// crops rather than letterboxes.

// ---- Deterministic PRNG so layouts are stable across renders ----
// (We can't use Math.random in the module body or hydration would
// disagree with the server-rendered output — though we're SPA-only
// here, a stable layout still avoids React warnings and makes the
// composition designed rather than incidental.)
function rng(seed: number): () => number {
  let s = seed >>> 0;
  return () => {
    s = (s * 1664525 + 1013904223) >>> 0;
    return s / 0x100000000;
  };
}

interface XY { x: number; y: number; }

// Spread N points across the canvas with a soft jitter so they
// don't sit on a visible grid. Returns positions in viewBox units.
function scatter(n: number, seed: number, padding = 40): XY[] {
  const r = rng(seed);
  const out: XY[] = [];
  // Quasi-grid: pick a rectangular cell count whose aspect roughly
  // matches the canvas, then perturb each cell center.
  const aspect = 1600 / 900;
  const rows = Math.max(2, Math.round(Math.sqrt(n / aspect)));
  const cols = Math.max(2, Math.ceil(n / rows));
  const cellW = (1600 - 2 * padding) / cols;
  const cellH = (900 - 2 * padding) / rows;
  outer: for (let row = 0; row < rows; row++) {
    for (let col = 0; col < cols; col++) {
      if (out.length >= n) break outer;
      const cx = padding + (col + 0.5) * cellW;
      const cy = padding + (row + 0.5) * cellH;
      out.push({
        x: cx + (r() - 0.5) * cellW * 0.9,
        y: cy + (r() - 0.5) * cellH * 0.9,
      });
    }
  }
  return out;
}

// Connect each node to its K nearest neighbors (mutually, deduped)
// so the mesh stays local and doesn't form long crossing lines.
function knnEdges(nodes: XY[], k: number): Array<[number, number]> {
  const dist2 = (a: XY, b: XY) => (a.x - b.x) ** 2 + (a.y - b.y) ** 2;
  const seen = new Set<string>();
  const edges: Array<[number, number]> = [];
  for (let i = 0; i < nodes.length; i++) {
    const order = nodes
      .map((_, j) => j)
      .filter((j) => j !== i)
      .sort((a, b) => dist2(nodes[i]!, nodes[a]!) - dist2(nodes[i]!, nodes[b]!))
      .slice(0, k);
    for (const j of order) {
      const key = i < j ? `${i}-${j}` : `${j}-${i}`;
      if (seen.has(key)) continue;
      seen.add(key);
      edges.push(i < j ? [i, j] : [j, i]);
    }
  }
  return edges;
}

// ---- Layout data (computed once at module load) ----
const FAR_NODES = scatter(54, 0x9F11);
const FAR_EDGES = knnEdges(FAR_NODES, 2);
const NEAR_NODES = scatter(28, 0x52CC);
const NEAR_EDGES = knnEdges(NEAR_NODES, 3);
const DUST = scatter(90, 0xDD08, 16);

// A handful of near-mesh edges that get a moving "data packet" pip.
// Indices into NEAR_EDGES; the dot animates along the line via
// SVG <animateMotion>.
const PACKET_EDGES = [3, 11, 18, 25, 32, 41].filter((i) => i < NEAR_EDGES.length);

// Binary rain columns — x position, vertical offset, animation delay,
// and the 0/1 string to render. We use SVG <text> on a <textPath>
// pulled by a vertical line so the text appears to fall.
const RAIN_COLS = [
  { x: 70,   delay: 0,   speed: 12, text: '01001101 11010010 00101101 10110100' },
  { x: 340,  delay: 4,   speed: 14, text: '11010100 01100110 10011010 01010110' },
  { x: 760,  delay: 7,   speed: 11, text: '10110011 00110101 11010010 01101001' },
  { x: 1180, delay: 2,   speed: 13, text: '01101011 11010100 10010110 01010011' },
  { x: 1480, delay: 9,   speed: 12, text: '10010110 01101010 11010011 01101100' },
];

// Reticle corner markers — purely decorative bracket + dot at the
// four extremes plus one near the upper-right where the artwork
// floats.
const RETICLES = [
  { cx: 80,   cy: 80,   corner: 'tl' },
  { cx: 1520, cy: 80,   corner: 'tr' },
  { cx: 80,   cy: 820,  corner: 'bl' },
  { cx: 1520, cy: 820,  corner: 'br' },
];

export function HeroBackground(): JSX.Element {
  return (
    <svg
      viewBox="0 0 1600 900"
      className="hero-bg"
      preserveAspectRatio="xMidYMid slice"
      aria-hidden="true"
    >
      <defs>
        <radialGradient id="hbg-node" cx="0.5" cy="0.5" r="0.5">
          <stop offset="0%"   stopColor="#cdf9ff" stopOpacity="1" />
          <stop offset="35%"  stopColor="#00f5ff" stopOpacity="0.85" />
          <stop offset="100%" stopColor="#00f5ff" stopOpacity="0" />
        </radialGradient>
        <radialGradient id="hbg-far" cx="0.5" cy="0.5" r="0.5">
          <stop offset="0%"   stopColor="#7bd8ff" stopOpacity="0.55" />
          <stop offset="100%" stopColor="#7bd8ff" stopOpacity="0" />
        </radialGradient>
        <radialGradient id="hbg-dust" cx="0.5" cy="0.5" r="0.5">
          <stop offset="0%"   stopColor="#c8f0ff" stopOpacity="0.7" />
          <stop offset="100%" stopColor="#c8f0ff" stopOpacity="0" />
        </radialGradient>

        {/* Linear-gradient stroke so the trace fades along its length —
            adds subtle directionality to the far-mesh edges. */}
        <linearGradient id="hbg-edge-far" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%"   stopColor="#3a8fb5" stopOpacity="0.0" />
          <stop offset="50%"  stopColor="#5ee3ff" stopOpacity="0.55" />
          <stop offset="100%" stopColor="#3a8fb5" stopOpacity="0.0" />
        </linearGradient>

        {/* Scan-sweep gradient — bright slim band that slides across. */}
        <linearGradient id="hbg-sweep" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%"   stopColor="#00f5ff" stopOpacity="0" />
          <stop offset="50%"  stopColor="#00f5ff" stopOpacity="0.35" />
          <stop offset="100%" stopColor="#00f5ff" stopOpacity="0" />
        </linearGradient>
      </defs>

      {/* ----- Layer 1: Star dust ----- */}
      <g className="hbg-dust-layer">
        {DUST.map((p, i) => (
          <circle
            key={i}
            cx={p.x}
            cy={p.y}
            r={i % 7 === 0 ? 1.4 : 0.8}
            fill="url(#hbg-dust)"
            className="dust"
            style={{ animationDelay: `${(i % 11) * 0.4}s`, animationDuration: `${4 + (i % 5)}s` }}
          />
        ))}
      </g>

      {/* ----- Layer 2: Far mesh ----- */}
      <g className="hbg-far-edges" stroke="url(#hbg-edge-far)" strokeWidth="0.6" fill="none">
        {FAR_EDGES.map(([a, b], i) => {
          const A = FAR_NODES[a]!;
          const B = FAR_NODES[b]!;
          return (
            <line
              key={i}
              x1={A.x} y1={A.y} x2={B.x} y2={B.y}
              className="far-edge"
              style={{ animationDelay: `${(i % 13) * 0.4}s` }}
            />
          );
        })}
      </g>
      <g className="hbg-far-nodes">
        {FAR_NODES.map((p, i) => (
          <circle
            key={i}
            cx={p.x}
            cy={p.y}
            r="6"
            fill="url(#hbg-far)"
            className="far-node"
            style={{ animationDelay: `${(i % 9) * 0.4}s` }}
          />
        ))}
      </g>

      {/* ----- Layer 3: Near mesh ----- */}
      <g
        className="hbg-near-edges"
        stroke="#5ee3ff"
        strokeWidth="0.8"
        strokeLinecap="round"
        fill="none"
      >
        {NEAR_EDGES.map(([a, b], i) => {
          const A = NEAR_NODES[a]!;
          const B = NEAR_NODES[b]!;
          return (
            <line
              key={i}
              x1={A.x} y1={A.y} x2={B.x} y2={B.y}
              className="near-edge"
              style={{ animationDelay: `${(i % 7) * 0.6}s` }}
            />
          );
        })}
      </g>
      <g className="hbg-near-nodes">
        {NEAR_NODES.map((p, i) => (
          <g
            key={i}
            className="near-node"
            style={{ animationDelay: `${(i % 6) * 0.5}s` }}
          >
            <circle cx={p.x} cy={p.y} r="14" fill="url(#hbg-node)" opacity="0.5" />
            <circle cx={p.x} cy={p.y} r="2"  fill="#dffaff" />
          </g>
        ))}
      </g>

      {/* ----- Layer 4: Data packets ----- */}
      <g className="hbg-packets" fill="#dffaff">
        {PACKET_EDGES.map((idx, i) => {
          const [a, b] = NEAR_EDGES[idx]!;
          const A = NEAR_NODES[a]!;
          const B = NEAR_NODES[b]!;
          // Build a single-segment path the packet rides on. We send
          // the same path back and forth via repeatCount="indefinite"
          // and a calcMode="linear" motion so it feels like sustained
          // traffic on that connection.
          const d = `M${A.x},${A.y} L${B.x},${B.y}`;
          const dur = 3 + i * 0.6;
          return (
            <g key={idx}>
              <circle r="2.4" opacity="0.9">
                <animateMotion dur={`${dur}s`} repeatCount="indefinite" path={d} />
              </circle>
              {/* Faint trailing dot for a comet-like read */}
              <circle r="1.2" opacity="0.5">
                <animateMotion
                  dur={`${dur}s`}
                  begin={`-${dur * 0.04}s`}
                  repeatCount="indefinite"
                  path={d}
                />
              </circle>
            </g>
          );
        })}
      </g>

      {/* ----- Layer 5: Binary rain ----- */}
      <g
        className="hbg-rain"
        fontFamily="'SFMono-Regular', Consolas, monospace"
        fontSize="11"
        fill="#5ee3ff"
        opacity="0.55"
      >
        {RAIN_COLS.map((col, i) => (
          <text
            key={i}
            x={col.x}
            y={-20}
            className="rain-col"
            style={{
              animationDuration: `${col.speed}s`,
              animationDelay: `${col.delay}s`,
            }}
          >
            {col.text.split('').map((ch, j) => (
              <tspan key={j} x={col.x} dy={j === 0 ? 0 : 14}>
                {ch}
              </tspan>
            ))}
          </text>
        ))}
      </g>

      {/* ----- Layer 6: Reticles + scan sweep ----- */}
      <g
        className="hbg-reticles"
        stroke="#00f5ff"
        strokeWidth="1.2"
        fill="none"
        opacity="0.6"
      >
        {RETICLES.map((r, i) => {
          // Bracket orientation per corner.
          const flip = r.corner === 'tr' || r.corner === 'br' ? -1 : 1;
          const flipY = r.corner === 'bl' || r.corner === 'br' ? -1 : 1;
          return (
            <g key={i} transform={`translate(${r.cx} ${r.cy})`}>
              <path d={`M0,0 L${20 * flip},0`} />
              <path d={`M0,0 L0,${20 * flipY}`} />
              <circle cx="0" cy="0" r="3" fill="#00f5ff" opacity="0.7" />
            </g>
          );
        })}
      </g>

      {/* Horizontal sweep band — wide, low-opacity, slow. */}
      <rect
        className="hbg-sweep"
        x="-300"
        y="0"
        width="300"
        height="900"
        fill="url(#hbg-sweep)"
      />
    </svg>
  );
}
